import { useEffect, useMemo, useState } from "react";
import type { Person, RoleDefinition, Skill } from "../services/api";
import {
  createPerson,
  deletePerson,
  listPeople,
  listRoles,
  listSkills,
  updatePerson,
  updatePersonSkills,
} from "../services/api";
import { PersonForm } from "./PersonForm";
import { PeopleTable } from "./PeopleTable";
import { SkillsSelector } from "./SkillsSelector";
import { Modal } from "./Modal";
import { getErrorAlertMessage } from "../utils/errors";

export function PeopleView() {
  const [people, setPeople] = useState<Person[]>([]);
  const [skills, setSkills] = useState<Skill[]>([]);
  const [roleDefinitions, setRoleDefinitions] = useState<RoleDefinition[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [editing, setEditing] = useState<Person | null>(null);
  const [assigningSkillsFor, setAssigningSkillsFor] = useState<Person | null>(null);
  const [filters, setFilters] = useState<{ region?: string; status?: string; roles?: string[]; skillCodes?: string[] }>({
    roles: [],
    skillCodes: [],
  });
  const [isSaving, setIsSaving] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);

  useEffect(() => {
    void refreshData();
    void refreshSkills();
    void refreshRoles();
  }, []);

  async function refreshData(activeFilters: typeof filters = filters) {
    setLoading(true);
    setError(null);
    try {
      const data = await listPeople({
        region: activeFilters.region,
        status: activeFilters.status,
        roles: activeFilters.roles && activeFilters.roles.length > 0 ? activeFilters.roles.join(",") : undefined,
        skills: activeFilters.skillCodes && activeFilters.skillCodes.length > 0 ? activeFilters.skillCodes.join(",") : undefined,
      });
      // Filter out root users from the CoE Pool view
      const filteredData = data.filter((person) => {
        const roles = person.roles || [];
        return !roles.some((role) => role.toLowerCase() === "root");
      });
      setPeople(filteredData);
    } catch (err) {
      setError(getErrorAlertMessage("load people", err));
    } finally {
      setLoading(false);
    }
  }

  async function refreshSkills() {
    try {
      const data = await listSkills();
      setSkills(data);
    } catch (err) {
      setError(getErrorAlertMessage("load skills", err));
    }
  }

  async function refreshRoles() {
    try {
      const data = await listRoles();
      setRoleDefinitions(data);
    } catch (err) {
      setError(getErrorAlertMessage("load role definitions", err));
    }
  }

  async function handleSubmit(person: Person) {
    setIsSaving(true);
    setError(null);
    try {
      if (editing) {
        await updatePerson(editing.email, person);
      } else {
        await createPerson(person);
      }
      await refreshData();
      closeModal();
    } catch (err) {
      setError(getErrorAlertMessage(editing ? "update this person" : "create this person", err));
    } finally {
      setIsSaving(false);
    }
  }

  async function handleDelete(person: Person) {
    if (!window.confirm(`Delete ${person.name}?`)) {
      return;
    }
    try {
      await deletePerson(person.email);
      await refreshData();
    } catch (err) {
      setError(getErrorAlertMessage("delete this person", err));
    }
  }

  function handleFiltersChange(nextFilters: typeof filters) {
    const shouldRefresh =
      nextFilters.region !== filters.region || nextFilters.status !== filters.status;
    setFilters(nextFilters);
    if (shouldRefresh) {
      void refreshData(nextFilters);
    }
  }

  function openCreateModal() {
    setEditing(null);
    setIsModalOpen(true);
  }

  function openEditModal(person: Person) {
    setEditing(person);
    setIsModalOpen(true);
  }

  function closeModal() {
    setIsModalOpen(false);
    setEditing(null);
  }

  async function handleSkillUpdate(email: string, skillCodes: string[]) {
    try {
      await updatePersonSkills(email, skillCodes);
      await refreshData();
      setAssigningSkillsFor(null);
    } catch (err) {
      setError(getErrorAlertMessage("update their skills", err));
    }
  }

  const regionOptions = useMemo(() => {
    const unique = new Set<string>();
    people.forEach((person) => {
      if (person.region) unique.add(person.region);
    });
    return Array.from(unique).sort();
  }, [people]);

  const statusOptions = useMemo(() => {
    const unique = new Set<string>();
    people.forEach((person) => {
      if (person.status) unique.add(person.status);
    });
    return Array.from(unique).sort();
  }, [people]);

  const filteredPeople = useMemo(() => {
    const roleFilters = (filters.roles ?? []).map((role) => role.toLowerCase());
    const skillCodeFilters = (filters.skillCodes ?? []).map((code) => code.toLowerCase());
    return people.filter((person) => {
      const personRoles = (person.roles ?? []).map((role) => role.toLowerCase());
      const personSkills = (person.skills ?? []).map((skill) => skill.toLowerCase());
      const matchesRoles =
        roleFilters.length === 0 || roleFilters.every((filterRole) => personRoles.includes(filterRole));
      const matchesSkills =
        skillCodeFilters.length === 0 || skillCodeFilters.every((filterSkill) => personSkills.includes(filterSkill));
      return matchesRoles && matchesSkills;
    });
  }, [people, filters.roles, filters.skillCodes]);

  return (
    <div className="layout single-column">
      <section className="panel">
        <div className="panel-header">
          <h2>People Filters</h2>
          <button type="button" className="ghost" onClick={() => void refreshData()} disabled={loading}>
            {loading ? "Refreshing…" : "Refresh"}
          </button>
        </div>
        <div className="form-grid">
          <label>
            Region
            <select value={filters.region ?? ""} onChange={(event) => handleFiltersChange({ ...filters, region: event.target.value || undefined })}>
              <option value="">All regions</option>
              {regionOptions.map((region) => (
                <option key={region} value={region}>
                  {region}
                </option>
              ))}
            </select>
          </label>
          <label>
            Status
            <select value={filters.status ?? ""} onChange={(event) => handleFiltersChange({ ...filters, status: event.target.value || undefined })}>
              <option value="">All statuses</option>
              {statusOptions.map((status) => (
                <option key={status} value={status}>
                  {status}
                </option>
              ))}
            </select>
          </label>
          <label>
            Roles
            <select
              multiple
              value={filters.roles ?? []}
              size={Math.min(8, Math.max(roleDefinitions.length, 4))}
              onChange={(event) => {
                const selections = Array.from(event.target.selectedOptions).map((option) => option.value);
                handleFiltersChange({ ...filters, roles: selections.length > 0 ? selections : undefined });
              }}
            >
              {roleDefinitions.map((role) => (
                <option key={role.role_code} value={role.role_code}>
                  {role.label}
                </option>
              ))}
            </select>
          </label>
          <label>
            Estimated Skills
            <select
              multiple
              value={filters.skillCodes ?? []}
              size={Math.min(8, Math.max(skills.length, 4))}
              onChange={(event) => {
                const selections = Array.from(event.target.selectedOptions).map((option) => option.value);
                handleFiltersChange({ ...filters, skillCodes: selections.length > 0 ? selections : undefined });
              }}
            >
              {skills.map((skill) => {
                const group = (skill.group_type ?? "GENERAL").toUpperCase();
                const label = `${group} -> ${skill.name ?? skill.skill_code}`;
                return (
                  <option key={skill.skill_code} value={skill.skill_code}>
                    {label}
                  </option>
                );
              })}
            </select>
          </label>
        </div>
        <div className="form-actions">
          <button type="button" className="primary" onClick={openCreateModal}>
            Add Person
          </button>
        </div>
      </section>
      <section className="content">
        {error && <div className="alert error">{error}</div>}
        {loading ? (
          <div className="panel">Loading people…</div>
        ) : (
          <PeopleTable
            people={filteredPeople}
            onEdit={openEditModal}
            onDelete={handleDelete}
            onSelectSkills={setAssigningSkillsFor}
          />
        )}
      </section>
      {assigningSkillsFor && (
        <SkillsSelector
          person={assigningSkillsFor}
          availableSkills={skills}
          onClose={() => setAssigningSkillsFor(null)}
          onSave={(skillCodes) => void handleSkillUpdate(assigningSkillsFor.email, skillCodes)}
        />
      )}
      <Modal open={isModalOpen} onClose={closeModal} title={editing ? "Edit Person" : "Add Person"}>
        <PersonForm
          initial={editing ?? undefined}
          availableRoles={roleDefinitions}
          onSubmit={handleSubmit}
          onCancel={closeModal}
          isSaving={isSaving}
        />
      </Modal>
    </div>
  );
}
